import React from "react";
import { useTheme, Box } from "@mui/material";
import MuiChip, { ChipProps } from "@mui/material/Chip";
import CheckIcon from "@mui/icons-material/Check";
import {
  baseChipStylesFn,
  iconOverlayStyles,
  iconWrapperStyles,
  chipTypeStyles,
} from "./Chip.styles";

import { COLORS } from "../../../constants/colors";
import { SxProps } from "@mui/system";

export interface Props {
  label?: string;
  chipType?: CHIP_TYPE;
  color?: "primary" | "secondary" | "default";
  variant?: "outlined" | "filled";
  disabled?: boolean;
  dragged?: boolean;
  selected?: boolean;
  hideBgMobile?: boolean;
  icon?: React.ReactElement;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  onDelete?: (e: React.MouseEvent<HTMLElement>) => void;
}

export type SayChipProps = Props & ChipProps;

export enum CHIP_TYPE {
  INPUT = "input",
  FILTER = "filter",
  CHOICE = "choice",
  ACTION = "action",
}

const Chip = ({
  label = "",
  chipType = CHIP_TYPE.FILTER,
  color = "default",
  variant = "filled",
  disabled,
  selected,
  hideBgMobile,
  dragged,
  icon,
  onClick,
  onDelete,
  ...rest
}: SayChipProps): JSX.Element => {
  const theme = useTheme();
  const baseStyles = baseChipStylesFn({
    breakpoints: theme.breakpoints,
    hideBgMobile,
  });

  /**
   * Styles the chip's custom Selected state (props.selected === true):
   * Different specs based on Chip variant, Say Chip type, and presence of icon.
   */
  const getSelectedStyles = (): SxProps => {
    if (variant === "outlined") return chipTypeStyles.outlinedSelected;
    else if (chipType === CHIP_TYPE.CHOICE)
      return chipTypeStyles.choiceDefaultSelected;
    else if (chipType === CHIP_TYPE.INPUT || onDelete) {
      return icon
        ? chipTypeStyles.inputDefaultSelectedWithIcon
        : chipTypeStyles.inputDefaultSelected;
    } else {
      return icon
        ? chipTypeStyles.filterDefaultSelectedWithIcon
        : chipTypeStyles.filterDefaultSelected;
    }
  };

  const consolidatedSelectedStyles = { ...baseStyles, ...getSelectedStyles() };
  const consolidatedDraggedStyles = {
    ...baseStyles,
    ...(variant === "outlined"
      ? chipTypeStyles.outlinedDragged
      : chipTypeStyles.defaultDragged),
  };
  const consolidatedStyles = {
    ...baseStyles,
    ...(chipType === CHIP_TYPE.ACTION ? chipTypeStyles.actionRoot : {}),
  };

  if (selected) {
    return (
      <React.Fragment>
        <MuiChip
          sx={consolidatedSelectedStyles}
          icon={
            icon ? (
              <Box sx={iconWrapperStyles}>
                <CheckIcon
                  sx={iconOverlayStyles}
                  style={{ color: COLORS.BLACK }}
                />
                {icon}
              </Box>
            ) : chipType === CHIP_TYPE.CHOICE ? undefined : (
              <CheckIcon style={{ color: theme.palette.primary.main }} />
            )
          }
          variant={variant}
          data-testid={`chip-${label}`}
          label={label}
          color={color}
          disabled={disabled}
          onClick={onClick}
          onDelete={onDelete}
          {...rest}
        />
      </React.Fragment>
    );
  }

  // Input and Filter chips have a custom Draggable state (props.selected === true):
  if (dragged) {
    return (
      <React.Fragment>
        <MuiChip
          sx={consolidatedDraggedStyles}
          variant={variant}
          label={label}
          color={color}
          disabled={disabled}
          data-testid={`chip-${label}`}
          onClick={onClick}
          onDelete={onDelete}
          icon={icon}
          {...rest}
        />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <MuiChip
        sx={consolidatedStyles}
        variant={variant}
        label={label}
        color={color}
        disabled={disabled}
        data-testid={`chip-${label}`}
        onClick={onClick}
        onDelete={onDelete}
        icon={icon}
        {...rest}
      />
    </React.Fragment>
  );
};

export default Chip;
